import { isEmpty } from 'lodash';
import { PropsWithChildren, useMemo } from 'react';

import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { DPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { useDPSuggestionKinds } from '@/modules/aiDP/hooks/useDPSuggestionKinds';
import { useOppositeDeathOrderSuggestions } from '@/modules/aiDP/hooks/useOppositeDeathOrderSuggestions';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useFirstClientDeathId } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { AiSuggestionKind } from '@/types/schema';

function useContextValue(): DPFormSuggestionsContext {
  const { loading, targetKind, suggestionsByKind } =
    useDispositiveProvisionSuggestionsContext();

  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();

  const firstDeathClientId = useFirstClientDeathId();

  const dpKinds = useDPSuggestionKinds({ firstDeathClientId, targetKind });

  const recipientSuggestions = useMemo(() => {
    if (!dpKinds) return undefined;

    const v2Suggestions = suggestionsByKind[AiSuggestionKind.DpRecipientV2];

    // KGV2 DP suggestions are keyed by firstDeathClientProfile and deathEvent
    // (instead of per-grantor suggestion kinds), so we filter per active death order.
    if (!isEmpty(v2Suggestions) && firstDeathClientId) {
      return v2Suggestions
        ?.filter(
          (s) =>
            s.dpRecipient &&
            s.dpFirstDeathGrantorClientProfile?.id === firstDeathClientId
        )
        .sort((a, b) => {
          const orderA = a.dpRecipient?.dispositionOrder ?? Infinity;
          const orderB = b.dpRecipient?.dispositionOrder ?? Infinity;
          if (orderA === orderB) return 0;
          return orderA - orderB;
        });
    }

    return suggestionsByKind[dpKinds.aiSuggestionKind]?.filter(
      (s) => s.dpRecipient
    );
  }, [dpKinds, firstDeathClientId, suggestionsByKind]);

  const unacknowledgedRecipientSuggestions = useMemo(() => {
    if (!dpKinds || !recipientSuggestions) return [];

    return recipientSuggestions.filter((s) => !acknowledgedSuggestions[s.id]);
  }, [acknowledgedSuggestions, dpKinds, recipientSuggestions]);

  const hasUnacknowledgedKGV2RecipientSuggestions = useMemo(() => {
    return unacknowledgedRecipientSuggestions.some(
      (suggestion) => suggestion.kind === AiSuggestionKind.DpRecipientV2
    );
  }, [unacknowledgedRecipientSuggestions]);

  const { hasSuggestions, hasUnacknowledgedRecipientSuggestions } =
    useMemo(() => {
      if (!dpKinds) {
        return {
          hasSuggestions: false,
          hasUnacknowledgedRecipientSuggestions: false,
        };
      }

      // Check if there are any recipient suggestions
      const hasRecipientSuggestions = !isEmpty(recipientSuggestions);

      // Check if there are any unacknowledged recipient suggestions
      const hasUnacknowledgedSuggestions = !isEmpty(
        unacknowledgedRecipientSuggestions
      );

      return {
        hasSuggestions: hasRecipientSuggestions,
        hasUnacknowledgedRecipientSuggestions: hasUnacknowledgedSuggestions,
      };
    }, [dpKinds, recipientSuggestions, unacknowledgedRecipientSuggestions]);

  // Get opposite death order suggestions info
  const {
    hasOppositeDeathOrderSuggestions,
    oppositeDeathOrderSuggestionsCount,
    oppositeDeathOrderClientName,
    oppositeDeathOrderClientId,
    isOppositeDeathOrderPrimary,
  } = useOppositeDeathOrderSuggestions();

  return {
    loading,
    dpSuggestionKinds: dpKinds,
    recipientSuggestions,
    hasSuggestions,
    hasUnacknowledgedRecipientSuggestions,
    hasUnacknowledgedKGV2RecipientSuggestions,
    unacknowledgedRecipientSuggestions,
    oppositeDeathOrderHasUnacknowledgedSuggestions:
      hasOppositeDeathOrderSuggestions,
    oppositeDeathOrderUnacknowledgedCount: oppositeDeathOrderSuggestionsCount,
    oppositeDeathOrderClientName,
    oppositeDeathOrderClientId,
    isOppositeDeathOrderPrimary,
  };
}

export function DPFormSuggestionsProvider({ children }: PropsWithChildren) {
  const contextValue = useContextValue();

  return (
    <DPFormSuggestionsContext.Provider value={contextValue}>
      {children}
    </DPFormSuggestionsContext.Provider>
  );
}
